import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import HotKeyListener from '@/components/ux/hot-key-listener';
import { toast } from '@/hooks/use-toast';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import type { VehicleListing } from '@/models/registry';
import { vehicleListingStore } from '@/models/stores/vehicle-listings';
import type { VehicleListingPurchaseStatus } from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import { BadgeCheck, BanknoteArrowDown, ShoppingCart } from 'lucide-react';
import { useMemo, useRef } from 'react';
import {
  ComboboxPopover,
  type Option,
} from '../../ui/combobox/combobox-popover';
import { VehiclePurchaseStatusBadge } from './vehicle-purchase-status-badge';

export const getStatusOptions = ({
  className,
}: {
  className: string;
}): Option<VehicleListingPurchaseStatus>[] => {
  return [
    {
      value: 'AVAILABLE',
      label: 'Available',
      icon: <BadgeCheck className={cn(className, 'text-blue-500')} />,
      hotkey: '1',
    },
    {
      value: 'PURCHASE_IN_PROGRESS',
      label: 'Purchase in Progress',
      icon: <ShoppingCart className={cn(className, 'text-orange-500')} />,
      hotkey: '2',
    },
    {
      value: 'SOLD',
      label: 'Sold',
      icon: <BanknoteArrowDown className={cn(className, 'text-green-500')} />,
      hotkey: '3',
    },
  ];
};

export const PURCHASE_STATUS_OPTIONS_MAP = getStatusOptions({
  className: 'h-3 w-3',
});

export type VehiclePurchaseStatusSelectorProps = {
  id: string;
  value: VehicleListing['purchase_status'];
  hideCopy?: boolean;
  side?: 'left' | 'right' | 'bottom' | 'top';
  withHotkeyListener?: boolean;
  buttonVariant?: 'outline' | 'ghost';
  className?: string;
};

export function VehiclePurchaseStatusSelector({
  id,
  value,
  hideCopy = false,
  side = 'left',
  withHotkeyListener = false,
  buttonVariant = 'ghost',
  className,
}: VehiclePurchaseStatusSelectorProps) {
  const statusButtonRef = useRef<HTMLButtonElement>(null);

  const updatePurchaseStatusMutation = useMutation({
    onMutate: async ({ purchase_status }) => {
      vehicleListingStore.updatePurchaseStatus({ id, purchase_status });
    },
    mutationFn: async ({
      purchase_status,
    }: {
      purchase_status: VehicleListing['purchase_status'];
    }) => {
      return await pdkAxios.post(`/inventory/listings/${id}/purchase_status`, {
        purchase_status,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error updating purchase status',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleStatusChange = (newStatus: VehicleListing['purchase_status']) => {
    updatePurchaseStatusMutation.mutate({ purchase_status: newStatus });
  };

  const currentOption = useMemo(() => {
    return PURCHASE_STATUS_OPTIONS_MAP.find((option) => option.value === value);
  }, [value]);

  return (
    <div>
      <ComboboxPopover
        endAdornment={
          <KeyboardKeyIcon>
            {HOTKEYS.VEHICLE_LISTING_PURCHASE_STATUS}
          </KeyboardKeyIcon>
        }
        placeholder="Set status..."
        currentOption={currentOption}
        onChange={handleStatusChange}
        emptyMessage="No status found"
        options={PURCHASE_STATUS_OPTIONS_MAP}
        side={side}
        align="start"
        onKeyDown={(e) => {
          const keyPressed = e.key;

          const option = PURCHASE_STATUS_OPTIONS_MAP.find(
            (option) => option.hotkey === keyPressed,
          );

          if (option) {
            handleStatusChange(option.value);
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <div>
          <VehiclePurchaseStatusBadge
            value={value ?? ''}
            hideCopy={hideCopy}
            buttonRef={statusButtonRef}
            buttonVariant={buttonVariant}
            className={cn('pl-0', className)}
          />
        </div>
      </ComboboxPopover>
      {withHotkeyListener && (
        <HotKeyListener
          hotkeys={HOTKEYS.VEHICLE_LISTING_PURCHASE_STATUS}
          onHotkeyPressed={() => {
            statusButtonRef.current?.click();
          }}
        />
      )}
    </div>
  );
}
